import { HttpErrorResponse } from '@angular/common/http';
import { ComponentRef, EventEmitter, Injectable } from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import { ProfileService } from '@barthauer/abc/core/authentication';
import {
  DataService,
  ObjectTypeInfo,
  QueryObjectsModel,
  QueryOutputMode,
  ReadListDataSourceModel,
  SaveQueryToDocumentModel,
} from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import {
  AbcCoreComponent,
  AbcCoreContainerService,
  AbcPopupComponent,
  AbcToastService,
  ToastSettings,
  _AbcTreeNode,
} from '@barthauer/abc/ui-components';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AbcQueriesEditorComponent } from './abc-queries-editor.component';
import { ResultsService } from './services/result.service';
import { ObjectSpaceInfo, Property, QueriesEditorResult, UI_SETTINGS } from './viewModels/abc-queries-editor.viewModel';
import { ResizeEndEvent, ResizeStartEvent } from 'devextreme/ui/popup';

@Injectable({
  providedIn: 'root',
})
export class AbcQueriesEditorService {
  public resourcesAsync: Promise<ResourceSet>;
  public resources: ResourceSet;

  public objectTypeInfosCacheMap: Map<string, ObjectTypeInfo[]> = new Map<string, ObjectTypeInfo[]>();

  private abcQueryEditorCptRef: ComponentRef<AbcQueriesEditorComponent>;
  private QueryEditorPopup: AbcPopupComponent;
  private QueryEditorContainer: AbcCoreComponent;
  private _rtlEnabled: boolean;
  private _queryDocumentId: string;
  private _queryDocument: QueryObjectsModel;
  private _objectSpaceInfos: ObjectSpaceInfo[] = [];
  private _objectSpaceName: string;
  private _rootElementName: string = '';
  private _queries: _AbcTreeNode<Property>;
  private _objectTypeInfos: ObjectTypeInfo[] = [];

  constructor(
    private dataService: DataService,
    private queriesEditorResultService: ResultsService,
    private abcCoreContainerService: AbcCoreContainerService,
    private resourcesService: ResourceManagerService,
    private messageBoxService: MessageBoxService,
    private abcToastService: AbcToastService,
    private profileService: ProfileService,
    private abcCore: AbcCore
  ) {
    this.resourcesAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
    this.resourcesAsync.then((resources) => {
      this.resources = resources;
    });
  }

  public get rtlEnabled(): boolean {
    return this._rtlEnabled;
  }
  public set rtlEnabled(value: boolean) {
    this._rtlEnabled = value;
  }

  public get queryDocumentId(): string {
    return this._queryDocumentId;
  }
  public set queryDocumentId(value: string) {
    this._queryDocumentId = value;
  }

  public get queryDocument(): QueryObjectsModel {
    return this._queryDocument;
  }
  public set queryDocument(value: QueryObjectsModel) {
    this._queryDocument = value;
  }

  public get objectSpaceInfos(): ObjectSpaceInfo[] {
    return this._objectSpaceInfos;
  }
  public set objectSpaceInfos(fields: ObjectSpaceInfo[]) {
    this._objectSpaceInfos = fields;
  }

  public set objectSpaceName(field: string) {
    this._objectSpaceName = field;
  }
  public get objectSpaceName(): string {
    return this._objectSpaceName;
  }

  public set rootElementName(alias: string) {
    this._rootElementName = alias;
  }
  public get rootElementName(): string {
    return this._rootElementName;
  }

  public set queries(queries: _AbcTreeNode<Property>) {
    this._queries = queries;
  }
  public get queries(): _AbcTreeNode<Property> {
    return this._queries;
  }

  public set objectTypeInfos(objectTypeInfos: ObjectTypeInfo[]) {
    this._objectTypeInfos = objectTypeInfos;
  }
  public get objectTypeInfos(): ObjectTypeInfo[] {
    return this._objectTypeInfos;
  }

  public getObjectTypeInfos(): Observable<ArrayBuffer> {
    return this.dataService.executeObjectTypesInfoQuery(this.preparQueryObjectType());
  }

  private preparQueryObjectType(): ReadListDataSourceModel {
    const queryObjectTypesInfo: ReadListDataSourceModel = new ReadListDataSourceModel();
    queryObjectTypesInfo.Indent = true;
    queryObjectTypesInfo.OutputMode = 0;
    queryObjectTypesInfo.ObjectSpaceName = this.objectSpaceName;
    return queryObjectTypesInfo;
  }

  public async runOpathQueriesEditor(queryId?: string, title?: string, execute?: boolean): Promise<void> {
    await this.runOpathQueriesEditorCore(undefined, queryId, title, execute);
  }

  private async getQueriesEditorResult(): Promise<QueriesEditorResult> {
    const queriesEditorResult = new QueriesEditorResult();
    queriesEditorResult.queryDocumentId = this.queryDocumentId;
    queriesEditorResult.objectSpaceName = this.objectSpaceName;
    queriesEditorResult.rootElementName = this.rootElementName;
    queriesEditorResult.objectQueries = await this.queriesEditorResultService.createObjectQueries(
      this.queries,
      this.queryDocument
    );
    queriesEditorResult.parameterDefinitions = this.queryDocument?.ParameterDefinitions;
    return queriesEditorResult;
  }

  public async saveQueryToDocument(): Promise<void> {
    const queriesEditorResult = await this.getQueriesEditorResult();
    const saveQueryDocumentToQuery = new SaveQueryToDocumentModel();
    saveQueryDocumentToQuery.Id = queriesEditorResult.queryDocumentId;
    saveQueryDocumentToQuery.Query = new QueryObjectsModel();
    saveQueryDocumentToQuery.Query.OutputMode = QueryOutputMode.Json;
    saveQueryDocumentToQuery.Query.Indent = true;
    saveQueryDocumentToQuery.Query.ObjectSpaceName = queriesEditorResult.objectSpaceName;
    saveQueryDocumentToQuery.Query.ObjectQueries = queriesEditorResult.objectQueries;
    saveQueryDocumentToQuery.Query.ParameterDefinitions = queriesEditorResult.parameterDefinitions;
    try {
      await this.dataService.executeSaveQueryToDocumentQuery(saveQueryDocumentToQuery).toPromise();

      const toastSettings: ToastSettings = new ToastSettings();
      toastSettings.width = '300px';
      toastSettings.type = 'success';
      toastSettings.position = 'top';
      toastSettings.message = this.resources.get('queryEditor-savingSuccess');

      this.abcToastService.showToast(toastSettings);
    } catch (error) {
      await this.showErrorMessage(error);
    }
  }

  public async closeOpathQueriesEditor(): Promise<void> {
    this.QueryEditorPopup && this.QueryEditorPopup.instance.dispose();
    this.QueryEditorPopup && this.QueryEditorPopup.ngOnDestroy();
    this.abcQueryEditorCptRef.destroy();
    this.QueryEditorPopup = undefined;
    this.QueryEditorContainer = undefined;
  }

  private async openOpathQueriesEditor(): Promise<void> {
    if (!this.QueryEditorPopup) {
      this.QueryEditorContainer = this.abcCoreContainerService.createComponent(
        this.QueryEditorContainer,
        AbcPopupComponent
      );
      this.QueryEditorPopup = this.QueryEditorContainer.componentRef.instance as AbcPopupComponent;
      this.QueryEditorPopup.resizeEnabled = true;
      this.QueryEditorPopup.component = AbcQueriesEditorComponent;
      this.QueryEditorPopup.width = '80%';
      this.QueryEditorPopup.height = '80%';
      this.QueryEditorPopup.minWidth = '650px';
      this.QueryEditorPopup.minHeight = '650px';
    }
  }

  private async showErrorMessage(response: HttpErrorResponse): Promise<void> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Error;
    settings.icon = MessageBoxIcon.Error;
    settings.button = MessageBoxButtons.OK;
    settings.message = response.error.Message;
    if (response.error.InnerException) {
      settings.errorMessage =
        response.error.InnerException.Message +
        `\n\n` +
        response.error.InnerException.ExceptionType +
        `\n\n` +
        response.error.InnerException.ExceptionMessage;
    } else {
      settings.message = this.resources.get('queryEditor-savingFailure');
    }

    this.messageBoxService.showMessageBox(settings).subscribe((result: DialogResult) => {
      if (DialogResult.OK === result['result']) {
        return;
      }
    });
  }

  public async executeQuery(query: QueryObjectsModel, title: string): Promise<void> {
    await this.runOpathQueriesEditorCore(query, undefined, title, true);
  }

  public async runOpathQueriesEditorCore(
    query: QueryObjectsModel,
    queryId: string,
    title?: string,
    execute?: boolean
  ): Promise<void> {
    await this.openOpathQueriesEditor();
    this.abcQueryEditorCptRef = this.QueryEditorPopup.openPopUp() as ComponentRef<AbcQueriesEditorComponent>;
    this.abcQueryEditorCptRef.instance.objectSpaceInfos = (await this.profileService.getAvailableObjectSpaces())?.map(
      (_) => <ObjectSpaceInfo>{ name: _.displayText, value: _.name }
    );
    this.queryDocument = query;
    this.queryDocumentId = queryId;
    this.abcQueryEditorCptRef.instance.parentResizeEvent = this.QueryEditorPopup
      .onResize as EventEmitter<ResizeStartEvent>;
    this.abcQueryEditorCptRef.instance.parentResizeEndEvent = this.QueryEditorPopup
      .onResizeEnd as EventEmitter<ResizeEndEvent>;
    this.abcQueryEditorCptRef.instance.parentMinHeight = 650;
    this.abcQueryEditorCptRef.instance.executeOnly = execute;
    this.abcQueryEditorCptRef.instance.rtlEnabled = this.rtlEnabled ?? true;
    this.QueryEditorPopup.title = title ?? this.resources.get('queryEditor-title');
    this.QueryEditorPopup.minWidth =
      UI_SETTINGS.PROPERTIES_Min_WIDTH +
      UI_SETTINGS.SELECTION_Min_WIDTH +
      UI_SETTINGS.TREE_Min_WIDTH +
      UI_SETTINGS.SPLITTER_MIN_TOLERENCE;
    this.QueryEditorPopup.waitForHide(this.abcCore.error, async () => await this.closeOpathQueriesEditor());
  }
}
